const SCRIV_DEFAULT_HOST = "https://scriv.ctpublic.org";

function resolveDefaultHost() {
  try {
    const script = document.querySelector('script[src*="scriv-chat"]');
    if (script && script.src) {
      return new URL(script.src).origin;
    }
  } catch (err) {
    // fall through to the constant
  }
  return SCRIV_DEFAULT_HOST;
}
const VALID_POSITIONS = ["left", "center", "right"];
const VALID_MODES = ["floating", "inline"];
const VALID_BUTTON_STYLES = ["adaptive", "label", "icon"];
const CONFIG_TIMEOUT_MS = 4000;
let panelIdCounter = 0;

// Launcher settings resolve in this order: an attribute on the element, then the bot's saved
// embed settings (fetched from Scriv once per page), then these defaults. Keys are attribute names.
const SETTING_DEFAULTS = {
  "button-text": "Chat",
  "button-style": "adaptive",
  "button-color": "#1677b8",
  "button-text-color": "#ffffff",
  position: "right",
  minimizable: "true",
  "hide-on-mobile": "false",
  "sticky-selector": "",
  "z-index": "",
};
const SETTING_NAMES = Object.keys(SETTING_DEFAULTS);

const ICONS = {
  chat: `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M4.804 21.644A6.707 6.707 0 0 0 6 21.75a6.721 6.721 0 0 0 3.583-1.029c.774.182 1.584.279 2.417.279 5.322 0 9.75-3.97 9.75-9 0-5.03-4.428-9-9.75-9s-9.75 3.97-9.75 9c0 2.409 1.025 4.587 2.674 6.192.232.226.277.428.254.543a3.73 3.73 0 0 1-.814 1.686.75.75 0 0 0 .44 1.223ZM8.25 10.875a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25ZM10.875 12a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875-1.125a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Z"/>
  </svg>`,
  minimize: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="3" stroke="currentColor" aria-hidden="true">
    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>`,
  close: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>`,
  chevronUp: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
    <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
  </svg>`,
  chevronDown: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
    <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
  </svg>`,
};

// Phones get the compact launcher, a 16px gutter and a full-screen panel. The `min-width: 640px`
// panel rule below is its complement.
const PHONE_QUERY = '(max-width: 639px)';

const STYLES = `
  :host {
    --scriv-z-index: 2147483000;
    --scriv-gap: 20px;
    --scriv-sticky-height: 0px;
    --scriv-safe-bottom: env(safe-area-inset-bottom, 0px);
    --scriv-bottom: calc(var(--scriv-sticky-height) + var(--scriv-gap) + var(--scriv-safe-bottom));
    --scriv-launcher-size: 48px;
    --scriv-button-color: #1677b8;
    --scriv-button-text-color: #ffffff;
    /* A UI face, not the page's body font, which on news sites is often a serif. A page can set
       --scriv-font-family on the element to match its own interface type. */
    --scriv-font: var(--scriv-font-family, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif);

    display: block;
    z-index: var(--scriv-z-index);
    position: fixed;
    bottom: var(--scriv-bottom);
  }

  /* Riding on top of a sticky bar: the bar already clears the home indicator. */
  :host(.has-sticky) {
    --scriv-safe-bottom: 0px;
  }

  :host(.position-left) {
    left: calc(var(--scriv-gap) + env(safe-area-inset-left, 0px));
  }

  :host(.position-right) {
    right: calc(var(--scriv-gap) + env(safe-area-inset-right, 0px));
  }

  /* No transform here: it would become the containing block of the fixed panel. */
  /* !important: page rules outrank :host rules, and a "* { margin: 0 }" reset would pin this left.
     Important declarations in a shadow tree beat the page's. */
  :host(.position-center) {
    left: 0;
    right: 0;
    width: fit-content;
    margin-inline: auto !important;
  }

  :host(.is-minimized.position-left) {
    left: env(safe-area-inset-left, 0px);
  }

  :host(.is-minimized.position-right) {
    right: env(safe-area-inset-right, 0px);
  }

  :host(.is-minimized.position-center) {
    bottom: calc(var(--scriv-sticky-height) + var(--scriv-safe-bottom));
  }

  :host(.mode-inline) {
    display: block;
    position: relative;
    bottom: auto;
    left: auto;
    right: auto;
    width: 100%;
    height: 100%;
    margin: 0;
  }

  :host(.mode-inline) .launcher,
  :host(.mode-inline) .minimize,
  :host(.mode-inline) .tab {
    display: none;
  }

  :host(.mode-inline) .panel {
    position: relative;
    top: auto;
    right: auto;
    bottom: auto;
    left: auto;
    width: 100%;
    height: 100%;
    max-height: none;
    transform: none;
    box-shadow: none;
  }

  :host(.mode-inline) .header .icon-btn {
    display: none;
  }

  .launcher,
  .minimize,
  .tab,
  .icon-btn {
    box-sizing: border-box;
    margin: 0;
    border: 0;
    font-family: var(--scriv-font);
    letter-spacing: normal;
    text-transform: none;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .launcher {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: var(--scriv-launcher-size);
    min-width: var(--scriv-launcher-size);
    max-width: calc(100vw - 2 * var(--scriv-gap));
    padding: 0 20px 0 16px;
    border-radius: 999px;
    background: var(--scriv-button-color);
    color: var(--scriv-button-text-color);
    font-size: 15px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12), 0 6px 16px rgba(0, 0, 0, 0.16);
    transition: box-shadow 0.2s ease, opacity 0.25s ease, transform 0.25s ease;
  }

  .launcher:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.14), 0 10px 24px rgba(0, 0, 0, 0.2);
  }

  .launcher-icon {
    display: inline-flex;
    flex: none;
  }

  .launcher-label {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .launcher-icon svg {
    display: block;
    width: 22px;
    height: 22px;
    fill: currentColor;
  }

  :host(.button-icon) .launcher {
    width: var(--scriv-launcher-size);
    padding: 0;
  }

  :host(.button-icon) .launcher-label {
    display: none;
  }

  :host(.button-icon) .launcher-icon svg {
    width: 24px;
    height: 24px;
  }

  /* Hover state layer in the text color, so it reads on any button color. */
  .launcher::after,
  .tab::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: currentColor;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease;
  }

  .launcher:hover::after,
  .tab:hover::after {
    opacity: 0.08;
  }

  .launcher:active::after,
  .tab:active::after {
    opacity: 0.14;
  }

  .launcher:focus-visible,
  .tab:focus-visible,
  .minimize:focus-visible {
    outline: none;
  }

  /* .pointer-focus: focus came back here after a tap, which WebKit would also ring (see _close). */
  :host(:not(.pointer-focus)) .launcher:focus-visible,
  :host(:not(.pointer-focus)) .tab:focus-visible,
  .minimize:focus-visible {
    box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px var(--scriv-button-color);
  }

  .minimize {
    position: absolute;
    top: -6px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    border-radius: 50%;
    background: #ffffff;
    color: #374151;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.3);
    opacity: 0;
    transform: scale(0.85);
    pointer-events: none;
    transition: opacity 0.15s ease, transform 0.15s ease;
  }

  /* 32px hit area around the 22px badge. */
  .minimize::before {
    content: "";
    position: absolute;
    inset: -5px;
  }

  .minimize svg {
    width: 12px;
    height: 12px;
  }

  :host(.position-left) .minimize,
  :host(.position-center) .minimize {
    right: -6px;
  }

  :host(.position-right) .minimize {
    left: -6px;
  }

  :host(:hover) .minimize,
  :host(:focus-within) .minimize {
    opacity: 1;
    transform: none;
    pointer-events: auto;
  }

  /* Touch screens have no hover to reveal it. */
  @media (hover: none) {
    .minimize {
      opacity: 1;
      transform: none;
      pointer-events: auto;
    }
  }

  :host(:not(.is-minimizable)) .minimize,
  :host(.is-minimized) .minimize,
  :host(.is-minimized) .launcher {
    display: none;
  }

  /* Minimized: a tab flush with the screen edge, clear of the page's text column. */
  .tab {
    position: relative;
    display: none;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: var(--scriv-launcher-size);
    padding: 0;
    background: var(--scriv-button-color);
    color: var(--scriv-button-text-color);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.14);
    transition: opacity 0.25s ease, transform 0.25s ease;
  }

  .tab svg {
    display: block;
    width: 16px;
    height: 16px;
    fill: currentColor;
  }

  :host(.is-minimized) .tab {
    display: flex;
  }

  :host(.position-left) .tab {
    border-radius: 0 12px 12px 0;
  }

  :host(.position-right) .tab {
    border-radius: 12px 0 0 12px;
  }

  :host(.position-center) .tab {
    width: var(--scriv-launcher-size);
    height: 28px;
    border-radius: 12px 12px 0 0;
  }

  /* Hidden until the saved settings arrive, so the launcher never jumps or recolors. */
  :host(:not(.is-ready)) .launcher,
  :host(:not(.is-ready)) .minimize,
  :host(:not(.is-ready)) .tab {
    visibility: hidden;
    opacity: 0;
    transform: translateY(8px);
  }

  :host(.is-open) .launcher,
  :host(.is-open) .minimize,
  :host(.is-open) .tab {
    visibility: hidden;
  }

  @media (prefers-reduced-motion: reduce) {
    :host(:not(.is-ready)) .launcher,
    :host(:not(.is-ready)) .minimize,
    :host(:not(.is-ready)) .tab {
      transform: none;
    }
  }

  .panel {
    position: fixed;
    box-sizing: border-box;
    width: 100%;
    background: white;
    border: 1px solid #e5e7eb;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16), 0 2px 6px rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    z-index: var(--scriv-z-index);
    max-height: calc(100vh - var(--scriv-bottom) - 12px);
  }

  .panel:focus {
    outline: none;
  }

  .panel.normal { height: 60%; }
  .panel.expanded { height: calc(100vh - var(--scriv-bottom) - 12px); }

  .panel.left { left: 0; bottom: var(--scriv-bottom); }
  .panel.right { right: 0; bottom: var(--scriv-bottom); }
  /* Rises from the launcher like the corner panels; centered vertically it floated loose and,
     expanded, ran under a sticky bar. */
  .panel.center { left: 50%; bottom: var(--scriv-bottom); transform: translateX(-50%); }

  @media (min-width: 640px) {
    .panel { width: 450px; }
    .panel.left { left: calc(var(--scriv-gap) + env(safe-area-inset-left, 0px)); }
    .panel.right { right: calc(var(--scriv-gap) + env(safe-area-inset-right, 0px)); }
  }

  .header {
    display: flex;
    flex: none;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 8px 8px 8px 16px;
    border-bottom: 1px solid #f3f4f6;
  }

  .header-title {
    overflow: hidden;
    font-family: var(--scriv-font);
    font-weight: 700;
    font-size: 16px;
    color: #111827;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .header-group {
    display: flex;
    flex: none;
    gap: 4px;
  }

  .icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
    border-radius: 6px;
    transition: background-color 0.2s;
    background: none;
    color: #6b7280;
  }

  .icon-btn:hover { background-color: #f3f4f6; }

  .icon-btn:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: 1px;
  }

  .icon-btn svg {
    width: 24px;
    height: 24px;
  }

  iframe {
    width: 100%;
    min-height: 0;
    flex-grow: 1;
    border: none;
    background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0nMjQnIGhlaWdodD0nMjQnIHZpZXdCb3g9JzAgMCAyNCAyNCcgeG1sbnM9J2h0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnJz48c3R5bGU+LnNwaW5uZXJfcU04M3thbmltYXRpb246c3Bpbm5lcl84SFFHIDEuMDVzIGluZmluaXRlfS5zcGlubmVyX29YUHJ7YW5pbWF0aW9uLWRlbGF5Oi4xc30uc3Bpbm5lcl9aVExme2FuaW1hdGlvbi1kZWxheTouMnN9QGtleWZyYW1lcyBzcGlubmVyXzhIUUd7MCUsNTcuMTQle2FuaW1hdGlvbi10aW1pbmctZnVuY3Rpb246Y3ViaWMtYmV6aWVyKDAuMzMsLjY2LC42NiwxKTt0cmFuc2Zvcm06dHJhbnNsYXRlKDApfTI4LjU3JXthbmltYXRpb24tdGltaW5nLWZ1bmN0aW9uOmN1YmljLWJlemllcigwLjMzLDAsLjY2LC4zMyk7dHJhbnNmb3JtOnRyYW5zbGF0ZVkoLTZweCl9MTAwJXt0cmFuc2Zvcm06dHJhbnNsYXRlKDApfX08L3N0eWxlPjxjaXJjbGUgY2xhc3M9J3NwaW5uZXJfcU04MycgY3g9JzQnIGN5PScxMicgcj0nMycvPjxjaXJjbGUgY2xhc3M9J3NwaW5uZXJfcU04MyBzcGlubmVyX29YUHInIGN4PScxMicgY3k9JzEyJyByPSczJy8+PGNpcmNsZSBjbGFzcz0nc3Bpbm5lcl9xTTgzIHNwaW5uZXJfWlRMZicgY3g9JzIwJyBjeT0nMTInIHI9JzMnLz48L3N2Zz4=);
    background-repeat: no-repeat;
    background-position: center;
  }

  @media ${PHONE_QUERY} {
    :host {
      --scriv-gap: 16px;
    }

    :host(.button-adaptive) .launcher {
      width: var(--scriv-launcher-size);
      padding: 0;
    }

    :host(.button-adaptive) .launcher-label {
      display: none;
    }

    :host(.button-adaptive) .launcher-icon svg {
      width: 24px;
      height: 24px;
    }

    :host(.hide-on-mobile) .launcher,
    :host(.hide-on-mobile) .minimize,
    :host(.hide-on-mobile) .tab {
      display: none;
    }

    /* A partial sheet leaves an unusable sliver of page and is crushed by the keyboard. The box
       follows the visual viewport (see _syncViewportTracking) so the header stays above it. */
    :host(.mode-floating) .panel {
      top: var(--scriv-viewport-top, 0px);
      right: auto;
      bottom: auto;
      left: var(--scriv-viewport-left, 0px);
      width: var(--scriv-viewport-width, 100%);
      height: var(--scriv-viewport-height, 100%);
      max-height: none;
      padding: env(safe-area-inset-top, 0px) env(safe-area-inset-right, 0px) env(safe-area-inset-bottom, 0px)
        env(safe-area-inset-left, 0px);
      border: 0;
      border-radius: 0;
      box-shadow: none;
      transform: none;
    }

    :host(.mode-floating) .icon-btn[data-action="toggle-size"] {
      display: none;
    }

    .icon-btn {
      padding: 8px;
    }
  }
`;

function toBool(value, fallback = false) {
  if (typeof value === 'boolean') return value;
  if (value === null || value === undefined) return fallback;
  return String(value).toLowerCase() === 'true';
}

function isColor(value) {
  if (typeof CSS !== "undefined" && typeof CSS.supports === "function") {
    return CSS.supports("color", value);
  }
  return /^#[0-9a-f]{6}$/i.test(value);
}

// The saved settings arrive as JSON (booleans, numbers, null); store them as the attribute strings
// they stand in for, dropping nulls so the defaults apply.
function normalizeRemoteConfig(data) {
  const config = {};
  if (!data || typeof data !== "object") return config;
  for (const name of SETTING_NAMES) {
    const value = data[name];
    if (value === null || value === undefined) continue;
    config[name] = String(value);
  }
  return config;
}

function timeoutSignal(ms) {
  if (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function") {
    return AbortSignal.timeout(ms);
  }
  return undefined;
}

class ScrivChat extends HTMLElement {
  static get observedAttributes() {
    return ['visible', 'expanded', 'mode', 'team', 'bot', 'scriv', 'no-button', ...SETTING_NAMES];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._visible = false;
    this._expanded = false;
    this._mode = 'floating';
    this._loaded = false;
    this._minimized = false;
    this._remoteConfig = {};
    this._configState = 'idle';
    this._ignoreAttributeChange = false;
    this._resizeObserver = null;
    this._stickyElement = null;
    this._windowResizeHandler = null;
    this._stickyTransitionHandler = null;
    this._messageHandler = null;
    this._viewportHandler = null;
    this._readingPosition = null;
    this._offsetTimeout = null;
    this._offsetRaf = null;
    this._built = false;
    this._panelId = `scriv-chat-panel-${++panelIdCounter}`;
    this._panelTitleId = `${this._panelId}-title`;
    this._elements = {};
  }

  connectedCallback() {
    // The move disconnects and reconnects the element; the second connect does the setup.
    if (this._belongsOnBody()) {
      this.ownerDocument.body.appendChild(this);
      return;
    }
    try {
      this._setVisible(this.getAttribute('visible'));
      this._setExpanded(this.getAttribute('expanded'));
      this._setMode(this.getAttribute('mode'));
      if (this._mode === 'inline') {
        this._setVisible('true', { skipReflect: true });
      }
      this._loaded = this._visible;
      this._minimized = this._readMinimized();
      this._buildDom();
      // _render applies the host classes that drive positioning on every call, including this
      // first one, so an element created with default attribute values still anchors correctly.
      this._render();
      if (this._mode !== 'inline') {
        this._initStickyDetection();
      }
      this._listenForChatMessages();
      this._loadRemoteConfig();
    } catch (err) {
      console.error('[scriv-chat] Initialization failed:', err);
    }
  }

  disconnectedCallback() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    if (this._stickyElement && this._stickyTransitionHandler) {
      this._stickyElement.removeEventListener('transitionend', this._stickyTransitionHandler);
    }
    if (this._offsetTimeout) {
      clearTimeout(this._offsetTimeout);
    }
    if (this._offsetRaf) {
      cancelAnimationFrame(this._offsetRaf);
    }
    if (this._windowResizeHandler) {
      window.removeEventListener('resize', this._windowResizeHandler);
      this._windowResizeHandler = null;
    }
    if (this._messageHandler) {
      window.removeEventListener('message', this._messageHandler);
      this._messageHandler = null;
    }
    this._stopViewportTracking();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (this._ignoreAttributeChange || oldValue === newValue) return;

    switch (name) {
      case 'visible':
        this._setVisible(newValue, { skipReflect: true });
        break;
      case 'expanded':
        this._setExpanded(newValue, { skipReflect: true });
        break;
      case 'mode':
        this._setMode(newValue, { skipReflect: true });
        break;
      case 'sticky-selector':
        if (this.isConnected && this._mode !== 'inline') this._initStickyDetection();
        break;
      default:
        break;
    }

    if (this._built) this._render();
  }

  get visible() { return this._visible; }
  set visible(value) { this._setVisible(value); this._render(); }

  get position() { return this._position(); }
  set position(value) { this.setAttribute('position', value); }

  get expanded() { return this._expanded; }
  set expanded(value) { this._setExpanded(value); this._render(); }

  get mode() { return this._mode; }
  set mode(value) { this._setMode(value); this._render(); }

  _setVisible(value, options = {}) {
    const next = toBool(value, false);
    if (this._visible === next && !options.forceRender) return;
    this._visible = next;
    if (next) this._loaded = true;
    if (!options.skipReflect) this._reflect('visible', next);
  }

  _setMode(value, options = {}) {
    const normalized = VALID_MODES.includes(String(value || '').toLowerCase())
      ? String(value || '').toLowerCase()
      : 'floating';
    if (this._mode === normalized && !options.forceRender) return;
    this._mode = normalized;
    if (!options.skipReflect) this._reflect('mode', normalized);
  }

  _setExpanded(value, options = {}) {
    const next = toBool(value, false);
    if (this._expanded === next && !options.forceRender) return;
    this._expanded = next;
    if (!options.skipReflect) this._reflect('expanded', next);
  }

  _reflect(attribute, value) {
    const stringValue = typeof value === 'boolean' ? (value ? 'true' : 'false') : value;
    const existing = this.getAttribute(attribute);
    if (existing === String(stringValue)) return;

    this._ignoreAttributeChange = true;
    if (stringValue === null || stringValue === undefined) {
      this.removeAttribute(attribute);
    } else {
      this.setAttribute(attribute, String(stringValue));
    }
    this._ignoreAttributeChange = false;
  }

  // --- Settings -----------------------------------------------------------

  _setting(name) {
    const attribute = this.getAttribute(name);
    if (attribute !== null && attribute !== '') return attribute;
    if (Object.prototype.hasOwnProperty.call(this._remoteConfig, name)) return this._remoteConfig[name];
    return SETTING_DEFAULTS[name];
  }

  _position() {
    const value = this._setting('position');
    return VALID_POSITIONS.includes(value) ? value : SETTING_DEFAULTS.position;
  }

  _buttonStyle() {
    const value = this._setting('button-style');
    return VALID_BUTTON_STYLES.includes(value) ? value : SETTING_DEFAULTS['button-style'];
  }

  _buttonText() {
    return this._setting('button-text') || SETTING_DEFAULTS['button-text'];
  }

  _flag(name) {
    return toBool(this._setting(name), toBool(SETTING_DEFAULTS[name]));
  }

  _color(name) {
    const value = this._setting(name);
    return isColor(value) ? value : SETTING_DEFAULTS[name];
  }

  _isMinimized() {
    return this._minimized && this._flag('minimizable');
  }

  _hasLauncher() {
    return this._mode !== 'inline' && !this.hasAttribute('no-button');
  }

  // A floating launcher pinned to the viewport must not sit inside page content: an ancestor with
  // a transform, `contain` or (Safari 17 and older) `container-type` anchors fixed descendants,
  // which put the launcher at the bottom of that block, far below the screen. Admin previews are
  // boxed in on purpose, and a launcher-less widget belongs to the page code that opened it.
  _belongsOnBody() {
    const body = this.ownerDocument.body;
    const inline = String(this.getAttribute('mode') || '').toLowerCase() === 'inline';
    return Boolean(body) && this.parentNode !== body && !inline
      && !this.hasAttribute('no-button') && !this.hasAttribute('preview');
  }

  async _loadRemoteConfig() {
    if (this._configState !== 'idle') return;
    const team = this.getAttribute('team');
    const bot = this.getAttribute('bot');
    const needsConfig = this._hasLauncher() && !this.hasAttribute('preview') && team && bot;
    if (!needsConfig || typeof fetch !== 'function') {
      this._configState = 'done';
      this._render();
      return;
    }

    this._configState = 'loading';
    const url = `${this._getHost()}/a/${encodeURIComponent(team)}/bots/b/${encodeURIComponent(bot)}/embed-config/`;
    try {
      const response = await fetch(url, { credentials: 'omit', signal: timeoutSignal(CONFIG_TIMEOUT_MS) });
      if (response.ok) {
        this._remoteConfig = normalizeRemoteConfig(await response.json());
      }
    } catch (err) {
      // Unreachable or slow: the attributes and defaults still make a working launcher.
    }
    this._configState = 'done';
    this._render();
    if (this.isConnected && this._mode !== 'inline' && this._remoteConfig['sticky-selector']) {
      this._initStickyDetection();
    }
  }

  // --- Visitor minimize ---------------------------------------------------

  _storageKey() {
    return `scriv-chat:${this.getAttribute('team') || ''}/${this.getAttribute('bot') || ''}:minimized`;
  }

  // Remembered per site and bot. Storage can throw (blocked cookies, sandboxed frames); the
  // in-memory state still works for the current page. Previews never persist.
  _readMinimized() {
    if (this.hasAttribute('preview')) return false;
    try {
      return window.localStorage.getItem(this._storageKey()) === '1';
    } catch (err) {
      return false;
    }
  }

  _writeMinimized(value) {
    if (this.hasAttribute('preview')) return;
    try {
      if (value) {
        window.localStorage.setItem(this._storageKey(), '1');
      } else {
        window.localStorage.removeItem(this._storageKey());
      }
    } catch (err) {
      // see _readMinimized
    }
  }

  _minimize() {
    // Keyboard users would lose focus with the × it sits on; a tap never focuses it, and moving
    // focus there would draw a focus ring nobody asked for.
    const hadFocus = this.shadowRoot.activeElement === this._elements.minimize;
    this._minimized = true;
    this._writeMinimized(true);
    this._render();
    if (hadFocus) this._elements.tab.focus({ preventScroll: true });
  }

  // --- Open / close -------------------------------------------------------

  _getHost() {
    return this.getAttribute('scriv') || resolveDefaultHost();
  }

  _toggle(options) {
    if (this._visible) {
      this._close(options);
    } else {
      this._open();
    }
  }

  _open() {
    // iOS scrolls the page hidden under the full-screen phone panel to reveal the focused chat
    // input; remember where the reader was so closing puts them back.
    const coversPage = this._mode !== 'inline' && Boolean(window.matchMedia?.(PHONE_QUERY).matches);
    this._readingPosition = coversPage ? window.scrollY : null;
    // Opening the chat from the tab undoes the minimize: closing brings back the full launcher.
    if (this._minimized) {
      this._minimized = false;
      this._writeMinimized(false);
    }
    this._setVisible(true, { forceRender: true });
    this._render();
    this._elements.panel.focus({ preventScroll: true });
  }

  _close({ fromPointer = false } = {}) {
    const focusWasInside = document.activeElement === this;
    this._setVisible(false, { forceRender: true });
    this._render();
    if (this._readingPosition != null) this._restoreReadingPosition(this._readingPosition);
    this._readingPosition = null;
    if (focusWasInside && this._hasLauncher()) {
      const { launcher } = this._elements;
      // Focus goes back for keyboard and screen-reader users either way. After a tap, WebKit
      // rings the programmatically focused launcher too, so hide the ring until a key is pressed.
      if (fromPointer) {
        this.classList.add('pointer-focus');
        launcher.addEventListener('blur', () => this.classList.remove('pointer-focus'), { once: true });
      }
      launcher.focus({ preventScroll: true });
    }
    this.dispatchEvent(new CustomEvent('scriv-close', { bubbles: true }));
  }

  _restoreReadingPosition(y) {
    const restore = () => {
      if (window.scrollY !== y) window.scrollTo(window.scrollX, y);
    };
    // Closed with the keyboard up: iOS scrolls the page again as the keyboard drops, so restore
    // once the viewport is back to full height (or after a second, if that never comes).
    const viewport = window.visualViewport;
    if (!viewport || viewport.height >= window.innerHeight - 1) {
      restore();
      return;
    }
    const finish = () => {
      viewport.removeEventListener('resize', onResize);
      clearTimeout(fallback);
      restore();
    };
    const onResize = () => {
      if (viewport.height >= window.innerHeight - 1) finish();
    };
    const fallback = setTimeout(finish, 1000);
    viewport.addEventListener('resize', onResize);
  }

  _toggleSize() {
    this._setExpanded(!this._expanded, { forceRender: true });
    this._render();
  }

  _listenForChatMessages() {
    if (this._messageHandler) return;
    // The chat runs in a cross-origin iframe, so Escape pressed inside it never reaches this
    // document; the chat posts a close request instead.
    this._messageHandler = (event) => {
      const { iframe } = this._elements;
      if (!iframe || event.source !== iframe.contentWindow) return;
      if (event.data && event.data.type === 'scriv:close-request' && this._visible && this._mode !== 'inline') {
        this._close();
      }
    };
    window.addEventListener('message', this._messageHandler);
  }

  // iOS does not shrink the layout viewport for the on-screen keyboard: it pans the visual
  // viewport to the focused input, which carries a full-screen fixed panel's header off the top.
  // While open, the phone panel takes the visual viewport's box instead (CSS variables, unused on
  // larger screens).
  _syncViewportTracking() {
    const viewport = window.visualViewport;
    const track = Boolean(viewport) && this._visible && this._mode !== 'inline';
    if (!track) {
      this._stopViewportTracking();
      return;
    }
    if (this._viewportHandler) return;
    this._viewportHandler = () => {
      this.style.setProperty('--scriv-viewport-top', `${viewport.offsetTop}px`);
      this.style.setProperty('--scriv-viewport-left', `${viewport.offsetLeft}px`);
      this.style.setProperty('--scriv-viewport-width', `${viewport.width}px`);
      this.style.setProperty('--scriv-viewport-height', `${viewport.height}px`);
    };
    viewport.addEventListener('resize', this._viewportHandler);
    viewport.addEventListener('scroll', this._viewportHandler);
    this._viewportHandler();
  }

  _stopViewportTracking() {
    if (!this._viewportHandler) return;
    window.visualViewport?.removeEventListener('resize', this._viewportHandler);
    window.visualViewport?.removeEventListener('scroll', this._viewportHandler);
    this._viewportHandler = null;
    for (const side of ['top', 'left', 'width', 'height']) {
      this.style.removeProperty(`--scriv-viewport-${side}`);
    }
  }

  // --- DOM ----------------------------------------------------------------

  _buildDom() {
    if (this._built) return;

    const style = document.createElement('style');
    style.textContent = STYLES;

    const launcher = document.createElement('button');
    launcher.type = 'button';
    launcher.className = 'launcher';
    launcher.setAttribute('aria-controls', this._panelId);
    launcher.setAttribute('aria-expanded', 'false');

    const launcherIcon = document.createElement('span');
    launcherIcon.className = 'launcher-icon';
    launcherIcon.innerHTML = ICONS.chat;

    const launcherLabel = document.createElement('span');
    launcherLabel.className = 'launcher-label';

    launcher.append(launcherIcon, launcherLabel);

    const minimize = document.createElement('button');
    minimize.type = 'button';
    minimize.className = 'minimize';
    minimize.innerHTML = ICONS.minimize;

    const tab = document.createElement('button');
    tab.type = 'button';
    tab.className = 'tab';
    tab.setAttribute('aria-controls', this._panelId);
    tab.setAttribute('aria-expanded', 'false');
    tab.innerHTML = ICONS.chat;

    const panel = document.createElement('div');
    panel.className = 'panel normal right';
    panel.style.display = 'none';
    panel.id = this._panelId;
    panel.tabIndex = -1;
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'false');
    panel.setAttribute('aria-labelledby', this._panelTitleId);

    const header = document.createElement('div');
    header.className = 'header';

    const headerTitle = document.createElement('div');
    headerTitle.className = 'header-title';
    headerTitle.id = this._panelTitleId;

    const headerGroup = document.createElement('div');
    headerGroup.className = 'header-group';

    const sizeBtn = document.createElement('button');
    sizeBtn.className = 'icon-btn';
    sizeBtn.type = 'button';
    sizeBtn.setAttribute('data-action', 'toggle-size');

    const closeBtn = document.createElement('button');
    closeBtn.className = 'icon-btn';
    closeBtn.type = 'button';
    closeBtn.setAttribute('data-action', 'close');
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = ICONS.close;
    closeBtn.title = 'Close';

    headerGroup.append(sizeBtn, closeBtn);
    header.append(headerTitle, headerGroup);

    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'Chat');

    panel.append(header, iframe);

    this.shadowRoot.append(style, launcher, minimize, tab, panel);

    this._elements = {
      launcher,
      launcherLabel,
      minimize,
      tab,
      panel,
      headerTitle,
      sizeBtn,
      closeBtn,
      iframe,
    };

    this._attachHandlers();
    this._built = true;
  }

  _updateHostClasses() {
    const position = this._position();
    const buttonStyle = this._buttonStyle();
    const classes = {
      'mode-floating': this._mode === 'floating',
      'mode-inline': this._mode === 'inline',
      'is-ready': this._configState === 'done',
      'is-open': this._visible && this._mode === 'floating',
      'is-minimizable': this._flag('minimizable'),
      'is-minimized': this._isMinimized(),
      'hide-on-mobile': this._flag('hide-on-mobile'),
    };
    for (const value of VALID_POSITIONS) classes[`position-${value}`] = value === position;
    for (const value of VALID_BUTTON_STYLES) classes[`button-${value}`] = value === buttonStyle;
    for (const [name, enabled] of Object.entries(classes)) {
      this.classList.toggle(name, enabled);
    }
  }

  _applyHostProperties() {
    this.style.setProperty('--scriv-button-color', this._color('button-color'));
    this.style.setProperty('--scriv-button-text-color', this._color('button-text-color'));
    const zIndex = this._setting('z-index');
    if (zIndex !== '' && Number.isFinite(Number(zIndex))) {
      this.style.setProperty('--scriv-z-index', String(Math.trunc(Number(zIndex))));
    } else {
      this.style.removeProperty('--scriv-z-index');
    }
  }

  _render() {
    if (!this._built) this._buildDom();

    const { launcher, launcherLabel, minimize, tab, panel, headerTitle, sizeBtn, iframe } = this._elements;
    const team = this.getAttribute('team') || '';
    const bot = this.getAttribute('bot') || '';
    const buttonText = this._buttonText();
    const host = this._getHost();

    this._updateHostClasses();
    this._applyHostProperties();
    this._syncViewportTracking();

    let iframeSrc = `${host}/a/${team}/bots/b/${bot}/chat/?embed=1`;
    const cssOverride = this.getAttribute('css-override');
    if (cssOverride) {
      iframeSrc += `&css=${encodeURIComponent(cssOverride)}`;
    }

    // Launcher, its minimize badge and the minimized tab. The label can be visually hidden
    // (icon styles, phones), so the accessible name is set explicitly.
    const display = this._hasLauncher() ? '' : 'none';
    const expanded = this._visible ? 'true' : 'false';
    launcherLabel.textContent = buttonText;
    launcher.setAttribute('aria-label', buttonText);
    launcher.setAttribute('aria-expanded', expanded);
    launcher.style.display = display;
    tab.setAttribute('aria-label', buttonText);
    tab.setAttribute('aria-expanded', expanded);
    tab.title = buttonText;
    tab.style.display = display;
    minimize.setAttribute('aria-label', `Minimize ${buttonText}`);
    minimize.title = `Minimize ${buttonText}`;
    minimize.style.display = display;

    // Update panel classes
    panel.className = [
      'panel',
      this._mode === 'inline' ? 'inline' : (this._expanded ? 'expanded' : 'normal'),
      this._mode === 'inline' ? '' : this._position(),
    ].filter(Boolean).join(' ');

    // Update header
    headerTitle.textContent = buttonText;
    sizeBtn.title = this._expanded ? 'Collapse' : 'Expand';
    sizeBtn.setAttribute('aria-label', sizeBtn.title);
    sizeBtn.innerHTML = this._expanded ? ICONS.chevronDown : ICONS.chevronUp;

    // Panel visibility without removing iframe
    panel.style.display = this._visible ? 'flex' : 'none';

    // Ensure iframe loaded once
    if (this._visible && this._loaded && !iframe.src) {
      iframe.src = iframeSrc;
    }
  }

  _attachHandlers() {
    const { launcher, minimize, tab, sizeBtn, closeBtn } = this._elements;

    // A click's detail is its click count: 0 when Enter or Space pressed the button.
    const byPointer = (event) => ({ fromPointer: event.detail > 0 });
    launcher.addEventListener('click', (event) => this._toggle(byPointer(event)));
    tab.addEventListener('click', (event) => this._toggle(byPointer(event)));
    minimize.addEventListener('click', () => this._minimize());
    sizeBtn.addEventListener('click', () => this._toggleSize());
    closeBtn.addEventListener('click', (event) => this._close(byPointer(event)));
    this.shadowRoot.addEventListener('keydown', (event) => {
      this.classList.remove('pointer-focus');
      if (event.key === 'Escape' && this._visible && this._mode !== 'inline') {
        this._close();
      }
    });
  }

  // --- Sticky bottom bar --------------------------------------------------

  _initStickyDetection() {
    // Clean up existing observer if any
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }

    // Set up window resize handler to re-detect sticky element
    if (!this._windowResizeHandler) {
      let resizeTimeout;
      this._windowResizeHandler = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          this._detectAndObserveStickyElement();
        }, 150); // Debounce resize events
      };
      window.addEventListener('resize', this._windowResizeHandler);
    }

    // Initial detection
    this._detectAndObserveStickyElement();
  }

  _findStickyElement() {
    const selector = this._setting('sticky-selector');
    if (!selector) return null;
    try {
      return document.querySelector(selector);
    } catch (err) {
      console.warn(`[scriv-chat] Ignoring invalid sticky-selector: ${selector}`);
      return null;
    }
  }

  _detectAndObserveStickyElement() {
    const element = this._findStickyElement();
    let isVisible = false;
    if (element) {
      const style = window.getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      isVisible =
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        (rect.width > 0 || rect.height > 0);
    }

    if (isVisible) {
      // Element found and visible
      if (this._stickyElement !== element) {
        // New element or first time finding it
        if (this._stickyElement && this._stickyTransitionHandler) {
          this._stickyElement.removeEventListener('transitionend', this._stickyTransitionHandler);
        }

        this._stickyElement = element;

        // Listen for transition end to re-evaluate after CSS transitions
        this._stickyTransitionHandler = () => this._scheduleOffsetUpdate();
        this._stickyElement.addEventListener('transitionend', this._stickyTransitionHandler);

        // Clean up old observer
        if (this._resizeObserver) {
          this._resizeObserver.disconnect();
        }

        // Watch for size changes
        if (typeof ResizeObserver !== 'undefined') {
          this._resizeObserver = new ResizeObserver(() => {
            this._scheduleOffsetUpdate();
          });
          this._resizeObserver.observe(this._stickyElement);
        }
      }

      this._scheduleOffsetUpdate();
    } else {
      // Element not found or hidden: sit at the default gap
      if (this._stickyElement) {
        // Was tracking an element, clean up
        if (this._stickyTransitionHandler) {
          this._stickyElement.removeEventListener('transitionend', this._stickyTransitionHandler);
          this._stickyTransitionHandler = null;
        }
        if (this._resizeObserver) {
          this._resizeObserver.disconnect();
          this._resizeObserver = null;
        }
        this._stickyElement = null;
      }
      this._setStickyHeight(0);
    }
  }

  _scheduleOffsetUpdate() {
    // Run an immediate measurement, another on the next frame, and a final one
    // after the player's transition completes (0.3s in CSS).
    if (!this._stickyElement) return;

    this._updateBottomOffset();

    if (this._offsetRaf) {
      cancelAnimationFrame(this._offsetRaf);
    }
    this._offsetRaf = requestAnimationFrame(() => this._updateBottomOffset());

    if (this._offsetTimeout) {
      clearTimeout(this._offsetTimeout);
    }
    this._offsetTimeout = setTimeout(() => this._updateBottomOffset(), 400);
  }

  _updateBottomOffset() {
    if (!this._stickyElement) return;

    const rect = this._stickyElement.getBoundingClientRect();

    // Use visual viewport when available to match what the user sees on mobile
    const viewportHeight = window.visualViewport?.height || window.innerHeight;

    // Calculate how far the element's bottom is from the viewport bottom
    const distanceFromBottom = viewportHeight - rect.bottom;

    const computedStyle = window.getComputedStyle(this._stickyElement);
    const position = computedStyle.position;
    const bottomValue = parseFloat(computedStyle.bottom);

    // Treat element as “at bottom” when it's fixed with a bottom offset near 0
    // or its rendered bottom is effectively touching the viewport bottom.
    const isFixedAtBottom = position === 'fixed' && !Number.isNaN(bottomValue) && bottomValue <= 1;
    const isVisuallyAtBottom = Math.abs(distanceFromBottom) <= 4;
    const isAtBottom = isFixedAtBottom || isVisuallyAtBottom;

    this._setStickyHeight(isAtBottom ? rect.height : 0);
  }

  _setStickyHeight(height) {
    if (height > 0) {
      this.style.setProperty('--scriv-sticky-height', `${height}px`);
    } else {
      this.style.removeProperty('--scriv-sticky-height');
    }
    this.classList.toggle('has-sticky', height > 0);
  }
}

if (!customElements.get('scriv-chat')) {
  customElements.define('scriv-chat', ScrivChat);
}

export { ScrivChat };
